import { useState, useEffect, useRef } from 'react';
import { DISCLAIMER_SECTIONS } from './Disclaimer';

/**
 * Hard-gate disclaimer pop-up.
 *
 * - Renders a full-viewport overlay; clicking outside does NOT dismiss it.
 * - Checkbox is disabled until the user scrolls to the bottom of the text.
 * - Accept button is disabled until both: scrolled-to-bottom AND checkbox-checked.
 * - When the user clicks Accept, `onAccept` is fired; the parent is responsible
 *   for closing the modal (by flipping its `isOpen` prop).
 *
 * Props:
 *   isOpen       — show/hide the modal
 *   onAccept     — callback fired when user clicks the Accept button
 *   actionLabel  — text on the Accept button (e.g. "Submit my review", "View reviews")
 */
export function DisclaimerModal({ isOpen, onAccept, actionLabel = 'Continue' }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [checked, setChecked] = useState(false);
  const scrollRef = useRef(null);

  // Reset state whenever the modal is reopened so each gate is a fresh ack.
  useEffect(() => {
    if (isOpen) {
      setScrolledToBottom(false);
      setChecked(false);
      // Scroll to top of disclaimer on open
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      }, 0);
    }
  }, [isOpen]);

  // Lock body scroll while modal is open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleScroll = (e) => {
    const t = e.currentTarget;
    // 12px slack — sub-pixel rounding makes exact-equality unreliable
    if (t.scrollHeight - t.scrollTop - t.clientHeight < 12) {
      setScrolledToBottom(true);
    }
  };

  const canAccept = scrolledToBottom && checked;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimer-modal-title"
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,44,78,0.55)',
        backdropFilter: 'blur(2px)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div style={{
        background: '#FAF6EC',
        borderRadius: 16,
        maxWidth: 760, width: '100%',
        maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 60px rgba(0,0,0,0.38)',
        border: '1px solid rgba(184,137,58,0.30)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid rgba(184,137,58,0.22)' }}>
          <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
            Before you continue
          </div>
          <h2 id="disclaimer-modal-title" style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 'clamp(20px, 3vw, 24px)',
            color: '#0F2C4E',
            fontWeight: 400,
            lineHeight: 1.2,
            margin: 0,
          }}>
            Legal Disclaimer &amp; Terms
          </h2>
          <p style={{ fontSize: 12, color: '#6B6358', marginTop: 8, marginBottom: 0, lineHeight: 1.5 }}>
            Please read the full disclaimer below. Scroll to the bottom to enable the acknowledgment checkbox.
          </p>
        </div>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            overflowY: 'auto',
            padding: '16px 24px 20px',
            flex: 1,
            background: '#FAF6EC',
          }}
        >
          {DISCLAIMER_SECTIONS.map((s, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0F2C4E', marginBottom: 4 }}>{s.title}</div>
              <p style={{ fontSize: 12.5, color: '#2C2C2C', lineHeight: 1.7, margin: 0 }}>{s.body}</p>
            </div>
          ))}
          {/* Sentinel at the bottom to make scroll-end detection more forgiving */}
          <div style={{ height: 4 }} aria-hidden="true" />
        </div>

        {/* Footer with checkbox + accept */}
        <div style={{
          padding: '14px 24px 18px',
          borderTop: '1px solid rgba(184,137,58,0.22)',
          background: '#F5EFE3',
        }}>
          {!scrolledToBottom && (
            <div style={{ fontSize: 11, color: '#B8893A', textAlign: 'center', marginBottom: 10, fontWeight: 600 }}>
              ↓ Scroll to the bottom to enable the checkbox
            </div>
          )}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            cursor: scrolledToBottom ? 'pointer' : 'not-allowed',
            opacity: scrolledToBottom ? 1 : 0.55,
            marginBottom: 12,
          }}>
            <input
              type="checkbox"
              disabled={!scrolledToBottom}
              checked={checked}
              onChange={e => setChecked(e.target.checked)}
              style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0, accentColor: '#0F2C4E' }}
            />
            <span style={{ fontSize: 12, color: '#2C2C2C', lineHeight: 1.6 }}>
              I have read and agree to the full Disclaimer above. I understand that RenterRep does not endorse, verify, or guarantee any review or individual on this platform; that I am reading and acting upon user content at my own sole risk; that I will conduct my own independent due diligence; and that no review on RenterRep constitutes a recommendation to rent or not rent to any person.
            </span>
          </label>
          <button
            type="button"
            onClick={onAccept}
            disabled={!canAccept}
            style={{
              width: '100%',
              background: canAccept ? '#0F2C4E' : '#9CA3AF',
              color: '#fff',
              border: 'none',
              padding: '13px',
              borderRadius: 50,
              fontSize: 14, fontWeight: 700,
              cursor: canAccept ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s, box-shadow 0.15s',
              boxShadow: canAccept ? '0 4px 12px rgba(15,44,78,0.20)' : 'none',
            }}
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
